/**
 * What the doorman DID, on the sender's side of a hearing (M11, FR-3.5, §1.8
 * loop 3).
 *
 * **NO MONEY FIGURE ANYWHERE ON THIS SURFACE** (ruling R-0825-3, owner
 * 2026-08-25; ruling R-0916-1). It used to carry the accounting: a hero figure
 * in credits and dollars, "Charged 330", "Unused 170 released", and an itemised
 * column of work units beside them. All of it is gone. The reason was never
 * squeamishness about the numbers — it was WHERE they were. This block renders
 * inside the thread the grilling happened in, which is the one conversation
 * this product promises will never be about money, and a bill at the foot of it
 * turned a piece of work somebody was proud of into an invoice they had to
 * check. The thread says the work happened; the plan screen says what share of
 * the period it came out of, afterwards, on the surface built for exactly that.
 *
 * The one sentence about money that survives is `covered_by_plan`, and it says
 * there is nothing to settle. A sender who has just watched a doorman work for
 * them deserves to be told that no bill is coming — an absence is not an answer
 * to a question somebody is actually asking.
 *
 * The units went with the figures, and that is the owner's call rather than a
 * consequence: an itemised column of quantities is an invoice's shape even when
 * none of its rows is money, and it invites the arithmetic the prose line
 * beneath it already answers better. "24 pages read · 7 of 9 claims verified ·
 * 12 sources" is the whole of what a sender wants to know.
 *
 * No score, no percentile, no band either: this is the sender's payload (§5.4
 * rule 5), so it reports work and never a verdict on it. Rendered from its props
 * alone. The one thing it still offers is the share affordance (M17, §1.8 loop
 * 3), which owns its own minting and renders nothing until it is asked for.
 */
import type { Receipt as ReceiptData } from "../../lib/api";
import { KnockShare, RECEIPT_SHARE_LABEL } from "../share/knock-share";
import { receiptPostText } from "../share/labels";

export const RECEIPT_HEADING = "What your knock bought";

/** The whole of what this surface says about money, and it says nothing is owed. */
export const COVERED_BY_PLAN = "Your plan covered it.";

/**
 * The work products, joined into one proud, dry line — only the counts that
 * happened. A hearing that bought no work has nothing to earn, and shows no line.
 */
export function earnedLine(earned: ReceiptData["receipts_earned"]): string {
  const parts: string[] = [];
  if (earned.pages_reviewed > 0) parts.push(plural(earned.pages_reviewed, "page") + " read");
  const verified = earned.claims_verified;
  const refuted = earned.claims_refuted;
  if (verified + refuted > 0) parts.push(`${verified} of ${verified + refuted} claims verified`);
  if (earned.sources_checked > 0) parts.push(plural(earned.sources_checked, "source"));
  return parts.join(" · ");
}

function plural(n: number, unit: string): string {
  return `${n} ${unit}${n === 1 ? "" : "s"}`;
}

type Props = {
  receipt: ReceiptData;
  /**
   * The knock this bill belongs to. Present only where the hearing is the
   * viewer's own and settled, which is what makes the receipt shareable (§1.8
   * loop 3); absent, the receipt is just a receipt.
   */
  knockId?: string;
};

export function Receipt({ receipt, knockId }: Props) {
  const earned = earnedLine(receipt.receipts_earned);
  /**
   * A hearing that bought no work at all has nothing to report and no heading
   * to put over it. Before the accounting came off there was always SOMETHING
   * to draw — a figure, if nothing else — and an empty frame was impossible.
   */
  if (!earned) return null;
  return (
    <div className="mt-8" role="group" aria-label={RECEIPT_HEADING}>
      <p className="font-mono text-[11px] uppercase tracking-[0.14em] text-faint">
        {RECEIPT_HEADING}
      </p>

      {/* The work, in one line of prose. Not a column of quantities: a table of
          numbers reads as a bill whether or not any of them is money, and this
          is the sentence somebody screenshots. */}
      <p className="mt-3 text-[15px] leading-relaxed text-text-soft">{earned}</p>

      {/* The one money sentence, and it is a closing rather than a statement of
          account: nothing is owed, so nothing follows it. Drawn only when the
          server says so — a client that assumed coverage would be promising on
          the server's behalf. */}
      {receipt.covered_by_plan && <p className="mt-1 text-sm text-muted">{COVERED_BY_PLAN}</p>}

      {/* Loop 3 (§1.8): the work is the ad. Quiet until it is asked for. */}
      {knockId && (
        <KnockShare
          kind="receipt"
          knockId={knockId}
          label={RECEIPT_SHARE_LABEL}
          postText={receiptPostText(receipt)}
          imageAlt={`Receipt card: ${earned}`}
        />
      )}
    </div>
  );
}
